import { gql } from "graphql-request";
import { createGraphQLClient } from "./graphql.service";

export interface PyloAro {
  id: string;
  entity: {
    data: {
      name: string;
      is_aro: boolean;
    };
  };
  instance_id?: string;

  parent?: {
    data: {
      id: string;
    };
  };
  children?: {
    data: Array<{ id: string }>;
  };
  pylo_users?: {
    data: Array<{ email: string; pylo_api_key?: { data: { name: string } } }>;
  };
}

export interface PyloAco {
  name?: string;
  id: string;
  entity?: {
    data: {
      id: string;
      name: string;
      is_system_entity: boolean;
    };
  };
  parent?: {
    data: {
      id: string;
    };
  };
  children?: {
    data: Array<{ id: string }>;
  };
}

export const getPyloAcos = async () => {
  const acosQueryDocument = gql`
    query acos {
      pyloAcoList(pagination: { page: 1, per_page: 2000 }) {
        data {
          id
          name
          entity {
            data {
              id
              name
              is_system_entity
            }
          }
          parent {
            data {
              id
            }
          }
        }
      }
    }
  `;
  const client = createGraphQLClient();

  try {
    const acos = (await client.request(acosQueryDocument)) as {
      pyloAcoList: {
        data: Array<PyloAco>;
      };
    };

    if (!acos) {
      throw Error("error fetching ACOs");
    }

    return acos.pyloAcoList;
  } catch (error) {
    console.error("error fetching ACOs", error);
    throw error;
  }
};

export interface EntityPermissions {
  has_create: boolean;
  has_read: boolean;
  has_update: boolean;
  has_delete: boolean;
}

// Any data flag already makes the entity's schema visible.
export const grantsSchema = (p: {
  has_create: boolean;
  has_read: boolean;
  has_update: boolean;
  has_delete: boolean;
  has_read_schema: boolean;
}) => p.has_read_schema || p.has_create || p.has_read || p.has_update || p.has_delete;

export interface PyloPermission {
  id: string;
  pylo_aro: {
    data: {
      id: string;
      instance_id?: string;
      entity?: {
        data: {
          name: string;
        };
      };
      parent?: {
        data: {
          id: string;
        };
      };
      pylo_users?: {
        data: Array<{
          email: string;
          pylo_api_key?: {
            data: {
              name: string;
            };
          };
        }>;
      };
    };
  };
  pylo_aco: {
    data: {
      id: string;
      name: string;
      entity: {
        data: {
          name: string;
        };
      };
    };
  };
  has_read: boolean;
  has_create: boolean;
  has_update: boolean;
  has_delete: boolean;
  has_read_schema: boolean;
  conditions_on_current?: string;
  conditions_on_patched?: string;
  // JSON-encoded array of relation paths (e.g. `["id","!image.url"]`); `["*"]` = all.
  field_paths?: string;
}

export const getPyloPermissions = async ({
  aroId,
  acoId,
  acoIds,
  page,
}: {
  aroId?: string;
  /** Single ACO match. Ignored when `acoIds` is provided. */
  acoId?: string;
  /** Match permissions whose ACO is any of these (used for inherited/ancestor view). */
  acoIds?: string[];
  page: number;
}) => {
  const client = createGraphQLClient({
    endpointName: "pyloPermissions",
  });

  // Build filter conditions based on provided parameters
  let filterQuery = "";

  if (aroId !== undefined || acoId !== undefined || (acoIds && acoIds.length > 0)) {
    const conditions = [];

    if (!!aroId) {
      conditions.push(`{condition: {field: "pylo_aro.id", operator: equal, value: "${aroId}"}}`);
    }

    if (acoIds && acoIds.length > 0) {
      const valueList = acoIds.map((id) => `"${id}"`).join(", ");
      conditions.push(`{condition: {field: "pylo_aco.id", operator: in, values: [${valueList}]}}`);
    } else if (!!acoId) {
      conditions.push(`{condition: {field: "pylo_aco.id", operator: equal, value: "${acoId}"}}`);
    }

    filterQuery = `(filter: { query: { and: [${conditions.join(", ")}] } }, pagination: { page: ${page}, per_page: 2000 })`;
  }

  // Define the query with conditional filter
  const query = gql`
    query permissions {
      pyloPermissionList ${filterQuery} {
        data {
          id
          pylo_aro {
            data {
              id
              instance_id
              entity {
                data {
                  id
                  name
                }
              }
              parent {
                data {
                  id
                }
              }
              pylo_users {
                data {
                  email
                  pylo_api_key {
                    data {
                      name
                    }
                  }
                }
              }

            }
          }
          pylo_aco {
            data {
              id
              name
              entity {
                data {
                  name
                }
              }
            }
          }
          has_read
          has_create
          has_update
          has_delete
          has_read_schema
          conditions_on_current
          conditions_on_patched
          field_paths
        }

        pagination {
        total
        has_more_pages
        current_page
        last_page
      }
      }
    }
  `;

  try {
    const permissions = (await client.request(query)) as {
      pyloPermissionList: {
        data: Array<PyloPermission>;
        pagination: {
          total: number;
          has_more_pages: boolean;
          current_page: number;
          last_page: number;
        };
      };
    };

    if (!permissions) {
      throw Error("error fetching permissions");
    }

    return permissions.pyloPermissionList;
  } catch (error) {
    console.error("error fetching permissions", error);
    throw error;
  }
};

interface CreatePermissionParams {
  aroId: string;
  acoId: string;
  hasRead?: boolean;
  hasCreate?: boolean;
  hasUpdate?: boolean;
  hasDelete?: boolean;
  hasReadSchema?: boolean;
  conditions?: string;
  field_paths?: string;
}

export const createPyloPermission = async ({
  aroId,
  acoId,
  hasRead = false,
  hasCreate = false,
  hasUpdate = false,
  hasDelete = false,
  hasReadSchema = false,
  conditions,
  field_paths,
}: CreatePermissionParams) => {
  const client = createGraphQLClient();

  const mutation = gql`
    mutation createPermission(
      $aroId: ID!
      $acoId: ID!
      $hasRead: Boolean!
      $hasCreate: Boolean!
      $hasUpdate: Boolean!
      $hasDelete: Boolean!
      $hasReadSchema: Boolean!
      $conditions: String
      $fieldPaths: String
    ) {
      createPyloPermission(
        input: {
          pylo_aro_set: { id: $aroId }
          pylo_aco_set: { id: $acoId }
          has_read: $hasRead
          has_create: $hasCreate
          has_update: $hasUpdate
          has_delete: $hasDelete
          has_read_schema: $hasReadSchema
          conditions_on_current: $conditions
          field_paths: $fieldPaths
        }
      ) {
        data {
          id
        }
      }
    }
  `;

  try {
    const result = await client.request(mutation, {
      aroId,
      acoId,
      hasRead,
      hasCreate,
      hasUpdate,
      hasDelete,
      hasReadSchema,
      conditions,
      fieldPaths: field_paths,
    });

    return result;
  } catch (error) {
    console.error("Error creating permission:", error);
    throw error;
  }
};

interface UpdatePermissionParams {
  id: string;
  hasRead?: boolean;
  hasCreate?: boolean;
  hasUpdate?: boolean;
  hasDelete?: boolean;
  hasReadSchema?: boolean;
  conditions_on_current?: string;
  conditions_on_patched?: string;
  field_paths?: string;
}

export const updatePyloPermission = async ({
  id,
  hasRead,
  hasCreate,
  hasUpdate,
  hasDelete,
  hasReadSchema,
  conditions_on_current,
  conditions_on_patched,
  field_paths,
}: UpdatePermissionParams) => {
  const client = createGraphQLClient();

  const updateInputFields = [];

  if (hasRead !== undefined) updateInputFields.push(`has_read: ${hasRead}`);
  if (hasCreate !== undefined) updateInputFields.push(`has_create: ${hasCreate}`);
  if (hasUpdate !== undefined) updateInputFields.push(`has_update: ${hasUpdate}`);
  if (hasDelete !== undefined) updateInputFields.push(`has_delete: ${hasDelete}`);
  if (hasReadSchema !== undefined) updateInputFields.push(`has_read_schema: ${hasReadSchema}`);
  if (conditions_on_current !== undefined) {
    const parsedConditions = JSON.parse(conditions_on_current);
    const escapedConditions = JSON.stringify(parsedConditions).replace(/"/g, '\\"');
    updateInputFields.push(`conditions_on_current: "${escapedConditions}"`);
  }
  if (conditions_on_patched !== undefined) {
    const parsedConditions = JSON.parse(conditions_on_patched);
    const escapedConditions = JSON.stringify(parsedConditions).replace(/"/g, '\\"');
    updateInputFields.push(`conditions_on_patched: "${escapedConditions}"`);
  }
  if (field_paths !== undefined) {
    const parsedPaths = JSON.parse(field_paths);
    const escapedPaths = JSON.stringify(parsedPaths).replace(/"/g, '\\"');
    updateInputFields.push(`field_paths: "${escapedPaths}"`);
  }
  updateInputFields.push(`id: "${id}"`);

  const updateInputContent = updateInputFields.join(", ");

  const mutation = gql`
    mutation updatePermission {
      updatePyloPermission(input: {
        ${updateInputContent}
      }) {
        data {
          id
        }
      }
    }
  `;

  try {
    const result = await client.request(mutation);
    return result;
  } catch (error) {
    console.error("Error updating permission:", error);
    throw error;
  }
};

interface CreateAcoParams {
  name?: string;
  parentId: string;
  entityId?: string;
}

export const createPyloAco = async ({ name, parentId, entityId }: CreateAcoParams) => {
  const client = createGraphQLClient();
  const mutation = gql`
    mutation createAco($input: CreatePyloAcoInput!) {
      createPyloAco(input: $input) {
        data {
          id
          name
          entity {
            data {
              id
              name
            }
          }
        }
      }
    }
  `;

  const input: Record<string, unknown> = {
    parent_set: { id: parentId },
  };

  if (name) {
    input.name = name;
  }

  if (entityId) {
    input.entity_set = { id: entityId };
  }

  try {
    const result = (await client.request(mutation, { input })) as {
      createPyloAco: {
        data: {
          id: string;
          name: string;
          entity?: { data: { id: string; name: string } };
        };
      };
    };
    return result.createPyloAco.data;
  } catch (error) {
    console.error("Error creating ACO:", error);
    throw error;
  }
};

interface UpdateAcoParams {
  id: string;
  name?: string;
  parentId?: string;
}

export const updatePyloAco = async ({ id, name, parentId }: UpdateAcoParams) => {
  const client = createGraphQLClient();
  const mutation = gql`
    mutation updateAco($input: UpdatePyloAcoInput!) {
      updatePyloAco(input: $input) {
        data {
          id
          name
        }
      }
    }
  `;
  const input = {
    id,
    name,
    parent_set: parentId ? { id: parentId } : undefined,
  };
  try {
    const result = (await client.request(mutation, { input })) as {
      updatePyloAco: { data: { id: string; name: string } };
    };
    return result.updatePyloAco.data;
  } catch (error) {
    console.error("Error updating ACO:", error);
    throw error;
  }
};

export const getMyPyloAros = async ({ userId }: { userId: string }) => {
  const myArosDocument = gql`
    query aros($userId: String!) {
      pyloAroList(
        pagination: { page: 1, per_page: 2000 }
        filter: {
          query: [
            {
              or: [
                { condition: { field: "pylo_users.id", operator: equal, value: $userId } }
                { condition: { field: "children.pylo_users.id", operator: equal, value: $userId } }
              ]
            }
          ]
        }
      ) {
        data {
          id
          entity {
            data {
              id
              name
            }
          }
          instance_id
          parent {
            data {
              id
            }
          }
          pylo_users {
            data {
              email
              pylo_api_key {
                data {
                  name
                }
              }
            }
          }
        }
      }
    }
  `;
  const client = createGraphQLClient({ endpointName: "myAros" });

  try {
    const aros = (await client.request(myArosDocument, { userId })) as {
      pyloAroList: {
        data: Array<PyloAro>;
      };
    };

    if (!aros) {
      throw Error("error fetching AROs");
    }

    return aros.pyloAroList;
  } catch (error) {
    console.error("error fetching AROs", error);
    throw error;
  }
};

export type AroTypeFilter = "users" | "apiKeys";

export interface PaginatedAroResponse {
  data: PyloAro[];
  pagination: {
    total: number;
    has_more_pages: boolean;
    current_page: number;
    last_page: number;
  };
}

// Builds `entity.name != X` filter conditions (one per name) to inject into an
// ARO list query's `and` block. Used to keep non-dashboard user entities (e.g.
// storefront `AppUser`) out of the entity-view sharing picker without pulling
// every ARO client-side. Returns "" when nothing is excluded so the permissions
// tree keeps its original behaviour.
const buildEntityExclusionConditions = (excludeEntityNames?: string[]): string =>
  (excludeEntityNames ?? [])
    .map((name) => `{ condition: { field: "entity.name", operator: notEqual, value: "${name}" } },`)
    .join("\n              ");

export const getRootAros = async ({
  page = 1,
  perPage = 50,
  excludeEntityNames,
}: {
  page?: number;
  perPage?: number;
  excludeEntityNames?: string[];
}): Promise<PaginatedAroResponse> => {
  const client = createGraphQLClient({ endpointName: "rootAros" });

  const entityExclusions = buildEntityExclusionConditions(excludeEntityNames);

  // Root AROs are parent groups - no type filter applied here
  // Type filter only applies to children (users vs API keys)
  const query = gql`
    query rootAros {
      pyloAroList(
        pagination: { page: ${page}, per_page: ${perPage} }
        filter: {
          query: {
            and: [
              { condition: { field: "parent.id", operator: isEmpty } },
              ${entityExclusions}
            ]
          }
        }
      ) {
        data {
          id
          entity {
            data {
              id
              name
            }
          }
          instance_id
          parent {
            data {
              id
            }
          }
          children {
            data {
              id
            }
          }
          pylo_users {
            data {
              email
              pylo_api_key {
                data {
                  name
                }
              }
            }
          }
        }
        pagination {
          total
          has_more_pages
          current_page
          last_page
        }
      }
    }
  `;

  try {
    const result = (await client.request(query)) as {
      pyloAroList: PaginatedAroResponse;
    };

    if (!result) {
      throw Error("error fetching root AROs");
    }

    return result.pyloAroList;
  } catch (error) {
    console.error("error fetching root AROs", error);
    throw error;
  }
};

export const getChildAros = async ({
  parentId,
  page = 1,
  perPage = 50,
  typeFilter,
  excludeEntityNames,
}: {
  parentId: string;
  page?: number;
  perPage?: number;
  typeFilter?: AroTypeFilter;
  excludeEntityNames?: string[];
}): Promise<PaginatedAroResponse> => {
  const client = createGraphQLClient({ endpointName: "childAros" });

  // Build type filter condition
  let typeFilterCondition = "";
  if (typeFilter === "apiKeys") {
    typeFilterCondition = `{ condition: { field: "pylo_users.pylo_api_key.id", operator: isNotEmpty } },`;
  } else if (typeFilter === "users") {
    typeFilterCondition = `{ condition: { field: "pylo_users.pylo_api_key.id", operator: isEmpty } },`;
  }

  const entityExclusions = buildEntityExclusionConditions(excludeEntityNames);

  const query = gql`
    query childAros {
      pyloAroList(
        pagination: { page: ${page}, per_page: ${perPage} }
        filter: {
          query: {
            and: [
              { condition: { field: "parent.id", operator: equal, value: "${parentId}" } },
              ${typeFilterCondition}
              ${entityExclusions}
            ]
          }
        }
      ) {
        data {
          id
          entity {
            data {
              id
              name
            }
          }
          instance_id
          parent {
            data {
              id
            }
          }
          children {
            data {
              id
            }
          }
          pylo_users {
            data {
              email
              pylo_api_key {
                data {
                  name
                }
              }
            }
          }
        }
        pagination {
          total
          has_more_pages
          current_page
          last_page
        }
      }
    }
  `;

  try {
    const result = (await client.request(query)) as {
      pyloAroList: PaginatedAroResponse;
    };

    if (!result) {
      throw Error("error fetching child AROs");
    }

    return result.pyloAroList;
  } catch (error) {
    console.error("error fetching child AROs", error);
    throw error;
  }
};

export const searchAros = async ({
  searchTerm,
  page = 1,
  perPage = 50,
  typeFilter,
  excludeEntityNames,
  signal,
}: {
  searchTerm: string;
  page?: number;
  perPage?: number;
  typeFilter?: AroTypeFilter;
  excludeEntityNames?: string[];
  signal?: AbortSignal;
}): Promise<PaginatedAroResponse> => {
  const client = createGraphQLClient({ endpointName: "searchAros" });

  const entityExclusions = buildEntityExclusionConditions(excludeEntityNames);

  // Build type filter and search field based on filter type
  // Users: search by email, filter by pylo_api_key.id isEmpty
  // API Keys: search by API key name, filter by pylo_api_key.id isNotEmpty
  let typeFilterCondition = "";
  let searchFieldCondition = "";

  if (typeFilter === "apiKeys") {
    typeFilterCondition = `{ condition: { field: "pylo_users.pylo_api_key.id", operator: isNotEmpty } },`;
    searchFieldCondition = `{ condition: { field: "pylo_users.pylo_api_key.name", operator: ilike, value: "%${searchTerm}%" } },`;
  } else {
    // Default to users
    typeFilterCondition = `{ condition: { field: "pylo_users.pylo_api_key.id", operator: isEmpty } },`;
    searchFieldCondition = `{ condition: { field: "pylo_users.email", operator: ilike, value: "%${searchTerm}%" } },`;
  }

  const query = gql`
    query searchAros {
      pyloAroList(
        pagination: { page: ${page}, per_page: ${perPage} }
        filter: {
          query: {
            and: [
              { condition: { field: "parent.id", operator: isNotEmpty } },
              ${searchFieldCondition}
              ${typeFilterCondition}
              ${entityExclusions}
            ]
          }
        }
      ) {
        data {
          id
          entity {
            data {
              id
              name
            }
          }
          instance_id
          parent {
            data {
              id
            }
          }
          children {
            data {
              id
            }
          }
          pylo_users {
            data {
              email
              pylo_api_key {
                data {
                  name
                }
              }
            }
          }
        }
        pagination {
          total
          has_more_pages
          current_page
          last_page
        }
      }
    }
  `;

  try {
    const result = (await client.request(query, undefined, { signal })) as {
      pyloAroList: PaginatedAroResponse;
    };

    if (!result) {
      throw Error("error searching AROs");
    }

    return result.pyloAroList;
  } catch (error) {
    // Don't log abort errors as they're expected when canceling queries
    if (error instanceof Error && error.name === "AbortError") {
      throw error;
    }
    console.error("error searching AROs", error);
    throw error;
  }
};

export const getAllApiKeys = async ({
  page = 1,
  perPage = 50,
  searchTerm,
  signal,
}: {
  page?: number;
  perPage?: number;
  searchTerm?: string;
  signal?: AbortSignal;
}): Promise<PaginatedAroResponse> => {
  const client = createGraphQLClient({ endpointName: "allApiKeys" });

  // Build search condition if search term provided
  const searchCondition = searchTerm
    ? `{ condition: { field: "pylo_users.pylo_api_key.name", operator: ilike, value: "%${searchTerm}%" } },`
    : "";

  const query = gql`
    query allApiKeys {
      pyloAroList(
        pagination: { page: ${page}, per_page: ${perPage} }
        filter: {
          query: {
            and: [
              { condition: { field: "parent.id", operator: isNotEmpty } },
              { condition: { field: "pylo_users.pylo_api_key.id", operator: isNotEmpty } },
              ${searchCondition}
            ]
          }
        }
      ) {
        data {
          id
          entity {
            data {
              id
              name
            }
          }
          instance_id
          parent {
            data {
              id
            }
          }
          children {
            data {
              id
            }
          }
          pylo_users {
            data {
              email
              pylo_api_key {
                data {
                  name
                }
              }
            }
          }
        }
        pagination {
          total
          has_more_pages
          current_page
          last_page
        }
      }
    }
  `;

  try {
    const result = (await client.request(query, undefined, { signal })) as {
      pyloAroList: PaginatedAroResponse;
    };

    if (!result) {
      throw Error("error fetching API keys");
    }

    return result.pyloAroList;
  } catch (error) {
    // Don't log abort errors as they're expected when canceling queries
    if (error instanceof Error && error.name === "AbortError") {
      throw error;
    }
    console.error("error fetching API keys", error);
    throw error;
  }
};

export const getAroById = async (id: string): Promise<PyloAro | null> => {
  const client = createGraphQLClient({ endpointName: "aroById" });

  const query = gql`
    query aroById {
      pyloAroList(
        pagination: { page: 1, per_page: 1 }
        filter: {
          query: [
            { condition: { field: "id", operator: equal, value: "${id}" } }
          ]
        }
      ) {
        data {
          id
          entity {
            data {
              id
              name
            }
          }
          instance_id
          parent {
            data {
              id
            }
          }
          children {
            data {
              id
            }
          }
          pylo_users {
            data {
              email
              pylo_api_key {
                data {
                  name
                }
              }
            }
          }
        }
      }
    }
  `;

  try {
    const result = (await client.request(query)) as {
      pyloAroList: {
        data: PyloAro[];
      };
    };

    return result.pyloAroList.data[0] ?? null;
  } catch (error) {
    console.error("error fetching ARO by id", error);
    throw error;
  }
};

/**
 * Fetch the current user's permissions in a single query, filtering directly by
 * the user relationship on the ARO (`pylo_aro.pylo_users.id`) and on the parent
 * ARO (`pylo_aro.children.pylo_users.id`) — mirroring how we filter AROs.
 *
 * This replaces the old two-step aros -> permissionsForAroIds waterfall on the
 * permissions-init path: it needs only the JWT user id (so it fires immediately
 * on mount), the DB dedupes each permission row, and it carries no ARO payload.
 * Returns the identical permission set as the aro-id approach.
 */
export const getMyPyloPermissions = async ({ userId }: { userId: string }) => {
  const client = createGraphQLClient({
    endpointName: "myPyloPermissions",
  });

  const query = gql`
    query myPermissions($userId: String!) {
      pyloPermissionList(
        pagination: { page: 1, per_page: 2000 }
        filter: {
          query: [
            {
              or: [
                { condition: { field: "pylo_aro.pylo_users.id", operator: equal, value: $userId } }
                {
                  condition: {
                    field: "pylo_aro.children.pylo_users.id"
                    operator: equal
                    value: $userId
                  }
                }
              ]
            }
          ]
        }
      ) {
        data {
          id
          pylo_aro {
            data {
              id
            }
          }
          pylo_aco {
            data {
              id
            }
          }
          has_read
          has_create
          has_update
          has_delete
          has_read_schema
          conditions_on_current
          conditions_on_patched
          field_paths
        }
      }
    }
  `;

  try {
    const permissions = (await client.request(query, { userId })) as {
      pyloPermissionList: {
        data: Array<PyloPermission>;
      };
    };

    if (!permissions) {
      throw Error("error fetching permissions");
    }

    return permissions.pyloPermissionList;
  } catch (error) {
    console.error("error fetching permissions", error);
    throw error;
  }
};
